import { clamp, len2, norm } from "../core/math.js";
import { CFG } from "./config.js";
import { createBullet, createPickup } from "./entities.js";
import { maybeDropPickup } from "./pickups.js";
import { cellToWorldCenter, damageWallAtWorld, damageWallCell, sampleTile, worldToCell } from "./world.js";
import { recordKill, updateAchievements } from "./stats.js";

function killEnemy(game, idx, sourceKind) {
  const e = game.enemies[idx];
  if (!e) return;
  const weaponKind = sourceKind || e.dotSourceKind || e.lastHitKind || "unknown";
  try {
    const enemyKind = e.kind || "unknown";
    recordKill({
      run: game.runStats,
      lifetime: game.lifetimeStats,
      weaponKind,
      enemyKind,
      enemyBossType: enemyKind === "boss" ? e.bossType || "summoner" : null,
    });
    updateAchievements(game.lifetimeStats);
  } catch {
    // ignore
  }
  game.audio?.explode?.(!!e.isBoss);
  game.enemies.splice(idx, 1);
  game.state.kills += 1;
  game.floats.push({ x: e.x, y: e.y - 18, ttl: 0.9, text: "+1" });
  // Boss progression is based on enemies killed (non-boss).
  if (!e.isBoss) game.state.bossKillsSince = (game.state.bossKillsSince || 0) + 1;

  // XP coins: small pieces that must be picked up.
  // Keep it cheap: cap coin count and avoid exceeding pickup limit.
  if (game.pickups.length <= CFG.maxPickups - 3) {
    const base = Math.max(1, Math.round(e.xp || 1));
    const n = Math.max(1, Math.min(3, base));
    const v = base / n;
    for (let k = 0; k < n; k++) {
      const ox = (Math.random() - 0.5) * 14;
      const oy = (Math.random() - 0.5) * 14;
      const p = createPickup({ x: e.x + ox, y: e.y + oy, kind: "coin", value: v });
      p.ttl = 9.5;
      game.pickups.push(p);
    }
  }

  triggerExplosiveAffix(game, e);
  if (game.state.gameOver) return;
  maybeDropPickup(game, e.x, e.y, e);
}

function triggerExplosiveAffix(game, e) {
  if (!e || e.affix !== "explosive") return;
  const r = (e.isBoss ? 72 : 48) + (e.kind === "tank" ? 8 : 0);
  const dmgMul = game.state.diff?.enemyDmgMul ?? 1;
  const splashEnemy = 18 + (game.state.wave || 1) * 0.45;

  if (game.bullets.length < CFG.maxBullets) {
    game.bullets.push({ x: e.x, y: e.y, vx: 0, vy: 0, dmg: 0, ttl: 0.24, r: 0, kind: "enemyExplosionFx", radius: r });
  }
  game.audio?.warning?.("explosive");

  const p = game.player;
  if (p && len2(e.x - p.x, e.y - p.y) <= (r + p.r) * (r + p.r)) {
    const boom = (14 + (game.state.wave || 1) * 0.55) * dmgMul;
    p.hp -= boom;
    game.state.hitFlash = Math.max(game.state.hitFlash || 0, 0.20);
    game.state.damageAngle = Math.atan2(e.y - p.y, e.x - p.x);
    game.state.damageT = 0.95;
    game.state.damageKind = "explosive";
    game.audio?.hit?.(true);
    game.floats.push({ x: p.x, y: p.y - 18, ttl: 0.8, text: `-${Math.round(boom)}` });
    if (p.hp <= 0) {
      p.hp = 0;
      game.endGame();
      return;
    }
  }

  // Chain reaction on nearby enemies.
  for (let j = game.enemies.length - 1; j >= 0; j--) {
    const o = game.enemies[j];
    if (len2(o.x - e.x, o.y - e.y) > (o.r + r * 0.85) * (o.r + r * 0.85)) continue;
    o.lastHitKind = "explosive";
    o.hp -= splashEnemy;
    if (o.hp <= 0) {
      killEnemy(game, j, "explosive");
      if (game.state.gameOver) return;
    }
  }
}

function dealEnemyDamage(game, e, dmg, dirX = 0, dirY = 0) {
  const p = game.player;
  let out = dmg;
  if (e.affix === "armored") out *= 0.72;

  // Shield enemy: reduce frontal hits (enemy "front" faces the player)
  if (e.kind === "shield" && (e.shieldMul || 0) > 0 && (dirX || dirY)) {
    const face = norm(p.x - e.x, p.y - e.y); // enemy -> player
    const inc = norm(dirX, dirY); // projectile direction (approx)
    const dot = inc.x * face.x + inc.y * face.y;
    // Incoming from the player's side => dot is negative (since projectile travels player->enemy).
    if (dot < -0.25) out *= e.shieldMul || 0.42;
  }

  // Execute: bonus damage under 20% HP
  if ((p?.buffs?.executeMul || 1) > 1 && (e.hpMax || 0) > 0) {
    const pct = e.hp / e.hpMax;
    if (pct <= 0.2) out *= p.buffs.executeMul || 1;
  }
  // Crit: chance + multiplier
  let crit = false;
  const cc = p?.buffs?.critChance || 0;
  if (cc > 0 && Math.random() < cc) {
    crit = true;
    out *= p?.buffs?.critMul || 1.6;
  }

  e.hp -= out;
  game.audio?.hit?.(false);
  game.floats.push({ x: e.x, y: e.y - 12, ttl: 0.8, text: `-${Math.round(out)}${crit ? "!" : ""}` });

  // Crit burn: applies a small burn DOT
  if (crit && p?.buffs?.critBurn) {
    const burnDps = 10;
    const burnT = 1.2;
    e.bleedT = Math.max(e.bleedT || 0, burnT);
    e.bleedDps = Math.max(e.bleedDps || 0, burnDps);
    e.dotKind = "burn";
    e.dotSourceKind = e.lastHitKind || e.dotSourceKind || "unknown";
  }
}

function distPointToSegmentSq(px, py, x0, y0, x1, y1) {
  const vx = x1 - x0;
  const vy = y1 - y0;
  const wx = px - x0;
  const wy = py - y0;
  const vv = vx * vx + vy * vy;
  const t = vv > 0.000001 ? clamp((wx * vx + wy * vy) / vv, 0, 1) : 0;
  const cx = x0 + vx * t;
  const cy = y0 + vy * t;
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy;
}

function bulletHitWall(mapId, x, y, brokenWalls) {
  const c = worldToCell(x, y);
  return sampleTile(mapId, c.cx, c.cy, brokenWalls).wall;
}

function wallDamageFromBullet(b) {
  const k = b.kind || "bullet";
  if (k === "laser") return 16;
  if (k === "lance") return 52;
  if (k === "shotgun") return 24;
  if (k === "flame") return 9;
  if (k === "boomerang") return 28;
  if (k === "mine") return 72;
  if (k === "turret") return 15;
  if (k === "tesla") return 0;
  return 12;
}

function bounceOrDestroyBullet({
  bullet,
  prevX,
  prevY,
  mapId,
  brokenWalls,
  ricochetChance = 0.35,
  dampMin = 0.72,
  dampMax = 0.90,
  onWallHit,
}) {
  if (!bulletHitWall(mapId, bullet.x, bullet.y, brokenWalls)) return "none";
  if (onWallHit) onWallHit();

  // Usually destroy; sometimes ricochet.
  if (Math.random() > ricochetChance) return "destroy";

  // Bounce based on which grid cell we entered from.
  const c0 = worldToCell(prevX, prevY);
  const c1 = worldToCell(bullet.x, bullet.y);
  const dx = c1.cx - c0.cx;
  const dy = c1.cy - c0.cy;

  // Place bullet back outside the wall to avoid getting stuck inside it.
  bullet.x = prevX;
  bullet.y = prevY;

  const damp = dampMin + Math.random() * (dampMax - dampMin);

  if (dx !== 0 && dy === 0) {
    bullet.vx *= -damp;
    bullet.vy *= damp;
  } else if (dy !== 0 && dx === 0) {
    bullet.vy *= -damp;
    bullet.vx *= damp;
  } else if (dx !== 0 && dy !== 0) {
    // Corner / big dt: bounce on the dominant axis (or random tie-breaker)
    if (Math.abs(bullet.vx) > Math.abs(bullet.vy)) bullet.vx *= -damp;
    else if (Math.abs(bullet.vy) > Math.abs(bullet.vx)) bullet.vy *= -damp;
    else (Math.random() < 0.5 ? (bullet.vx *= -damp) : (bullet.vy *= -damp));
  } else {
    // Same cell but inside wall (rare): flip a random axis.
    (Math.random() < 0.5 ? (bullet.vx *= -damp) : (bullet.vy *= -damp));
  }

  // Small penalty so infinite wall-bouncing doesn't happen.
  bullet.ttl *= 0.92;
  if (bullet.pierce > 0) bullet.pierce = Math.max(0, bullet.pierce - 1);
  // Synergy: ricochet makes the bullet a bit stronger.
  bullet.dmg *= 1.08;

  return "bounce";
}

export function updateEnemies(dt, game) {
  const { enemies, player, state } = game;
  // Progression: prefer "more enemies" over "much faster enemies".
  // Keep enemy movement scaling modest with wave, instead of using state.difficulty (which ramps hard).
  const w = state.wave || 1;
  const spMul = 1 + clamp((w - 1) * 0.018, 0, 0.75);
  const contactMul = 1 + clamp((w - 1) * 0.03, 0, 1.4);
  const dmgMul = state.diff?.enemyDmgMul ?? 1;

  for (let i = enemies.length - 1; i >= 0; i--) {
    const e = enemies[i];
    // Bleed / DOT
    if ((e.bleedT || 0) > 0) {
      e.bleedT = Math.max(0, e.bleedT - dt);
      e.hp -= (e.bleedDps || 0) * dt;

      // Synergy: burn spreads to nearby enemies
      if ((game.player?.buffs?.burnSpread || 0) > 0 && e.dotKind === "burn") {
        e.burnSpreadCd = Math.max(0, (e.burnSpreadCd || 0) - dt);
        if ((e.burnSpreadCd || 0) <= 0) {
          e.burnSpreadCd = 0.55;
          const level = game.player?.buffs?.burnSpread || 0;
          const r = 56 + level * 14;
          const r2 = r * r;
          for (let k = 0; k < enemies.length; k++) {
            const o = enemies[k];
            if (o === e) continue;
            if (o.dotKind === "burn" && (o.bleedT || 0) > 0) continue;
            if (len2(o.x - e.x, o.y - e.y) > r2) continue;
            o.bleedT = Math.max(o.bleedT || 0, Math.min(0.9, e.bleedT || 0));
            o.bleedDps = Math.max(o.bleedDps || 0, (e.bleedDps || 0) * 0.7);
            o.dotKind = "burn";
            break;
          }
        }
      }

      if (e.hp <= 0) {
        killEnemy(game, i, e.dotSourceKind || e.lastHitKind);
        continue;
      }
    }
    const toP = norm(player.x - e.x, player.y - e.y);
    const spd = e.speed * spMul;
    // default steering movement (can be overridden by special kinds below)
    e.vx = toP.x * spd;
    e.vy = toP.y * spd;

    // special: charger (wind-up then dash)
    if (e.kind === "charger") {
      if ((e.chargeWindT || 0) > 0) {
        e.chargeWindT = Math.max(0, (e.chargeWindT || 0) - dt);
        // stop while telegraphing
        e.vx = 0;
        e.vy = 0;
        if ((e.chargeWindT || 0) === 0) e.chargeT = 0.22;
      } else if ((e.chargeT || 0) > 0) {
        e.chargeT = Math.max(0, (e.chargeT || 0) - dt);
        const dashSp = spd * 4.2;
        e.vx = (e.chargeDx || toP.x) * dashSp;
        e.vy = (e.chargeDy || toP.y) * dashSp;
      } else {
        e.chargeCd = (e.chargeCd || 0) - dt;
        if ((e.chargeCd || 0) <= 0) {
          e.chargeCd = clamp(2.4 - state.wave * 0.03, 1.4, 2.4);
          e.chargeWindT = 0.28;
          e.chargeDx = toP.x;
          e.chargeDy = toP.y;
          game.audio?.warning?.("charge");
        }
      }
    }

    // special: summoner (keeps some distance, periodically spawns adds)
    if (e.kind === "summoner") {
      const dx = player.x - e.x;
      const dy = player.y - e.y;
      const d2 = dx * dx + dy * dy;
      const keepAway = 150;
      const dir = d2 < keepAway * keepAway ? norm(-dx, -dy) : toP;
      e.vx = dir.x * spd;
      e.vy = dir.y * spd;
      e.summonCd = (e.summonCd || 0) - dt;
      if ((e.summonCd || 0) <= 0) {
        e.summonCd = clamp(3.0 - state.wave * 0.04, 1.6, 3.0);
        const n = 2 + ((Math.random() * 2) | 0);
        for (let k = 0; k < n; k++) {
          game.spawnEnemyNear(e.x, e.y, Math.random() < 0.6 ? "fast" : "walker");
        }
      }
    }

    if (e.affix === "frenzy" && (e.hpMax || 0) > 0) {
      const hpN = clamp(e.hp / Math.max(1, e.hpMax), 0, 1);
      const frenzyMul = 1 + (1 - hpN) * 0.65;
      e.vx *= frenzyMul;
      e.vy *= frenzyMul;
    }
    e.x += e.vx * dt;
    e.y += e.vy * dt;

    // special: spitter shoots low-dmg bullets
    if (e.kind === "spitter") {
      // wind-up telegraph, then fire
      if ((e.windT || 0) > 0) {
        e.windT = Math.max(0, e.windT - dt);
        if (e.windT <= 0) {
          game.enemyBullets.push(
            createBullet({
              x: e.x,
              y: e.y,
              vx: (e.windDx || 0) * 240,
              vy: (e.windDy || 0) * 240,
              dmg: e.windDmg || (9 + state.wave * 0.6),
              ttl: 2.1,
              r: 3,
              kind: "spit",
            }),
          );
        }
      } else {
        e.shootCd -= dt;
        if (e.shootCd <= 0) {
          e.shootCd = clamp(1.9 - state.wave * 0.08, 0.6, 1.9);
          const dv = norm(player.x - e.x, player.y - e.y);
          e.windT = 0.28;
          e.windDx = dv.x;
          e.windDy = dv.y;
          e.windDmg = 9 + state.wave * 0.6;
          game.audio?.warning?.("shot");
        }
      }
    }

    // special: grenadier lobs grenades (telegraphed AoE) to force movement
    if (e.kind === "grenadier") {
      // keep distance
      const dx = player.x - e.x;
      const dy = player.y - e.y;
      const d2 = dx * dx + dy * dy;
      const keepAway = 190;
      const dir = d2 < keepAway * keepAway ? norm(-dx, -dy) : toP;
      e.vx = dir.x * spd;
      e.vy = dir.y * spd;

      if ((e.throwWindT || 0) > 0) {
        e.throwWindT = Math.max(0, (e.throwWindT || 0) - dt);
        // stop while aiming
        e.vx = 0;
        e.vy = 0;
        if ((e.throwWindT || 0) <= 0) {
          const r = 54 + state.wave * 0.3;
          game.enemyBullets.push({
            x: e.throwTx || player.x,
            y: e.throwTy || player.y,
            vx: 0,
            vy: 0,
            dmg: 22 + state.wave * 1.2,
            ttl: 0.75,
            r: 0,
            kind: "grenade",
            radius: r,
            armT: 0.75,
          });
        }
      } else {
        e.throwCd = (e.throwCd || 0) - dt;
        if ((e.throwCd || 0) <= 0) {
          e.throwCd = clamp(3.2 - state.wave * 0.03, 1.6, 3.2);
          e.throwWindT = 0.45;
          // predict slightly ahead of player movement
          const lead = 0.25;
          e.throwTx = player.x + (player.vx || 0) * lead;
          e.throwTy = player.y + (player.vy || 0) * lead;
          game.audio?.warning?.("aoe");
        }
      }
    }

    // special: pyro places a flame zone (DoT area)
    if (e.kind === "pyro") {
      const dx = player.x - e.x;
      const dy = player.y - e.y;
      const d2 = dx * dx + dy * dy;
      const keepAway = 160;
      const dir = d2 < keepAway * keepAway ? norm(-dx, -dy) : toP;
      e.vx = dir.x * spd;
      e.vy = dir.y * spd;

      if ((e.fireWindT || 0) > 0) {
        e.fireWindT = Math.max(0, (e.fireWindT || 0) - dt);
        e.vx = 0;
        e.vy = 0;
        if ((e.fireWindT || 0) <= 0) {
          game.enemyBullets.push({
            x: e.fireTx || player.x,
            y: e.fireTy || player.y,
            vx: 0,
            vy: 0,
            dmg: 0,
            ttl: 3.0,
            r: 0,
            kind: "fireZone",
            radius: 58,
            warmT: 0.35,
            dps: 12 + state.wave * 0.55,
          });
        }
      } else {
        e.fireCd = (e.fireCd || 0) - dt;
        if ((e.fireCd || 0) <= 0) {
          e.fireCd = clamp(3.0 - state.wave * 0.03, 1.5, 3.0);
          e.fireWindT = 0.38;
          const lead = 0.18;
          e.fireTx = player.x + (player.vx || 0) * lead;
          e.fireTy = player.y + (player.vy || 0) * lead;
          game.audio?.warning?.("aoe");
        }
      }
    }

    // --- Boss patterns (variety via e.bossType) ---
    if (e.isBoss) {
      const bt = e.bossType || "summoner";

      // Titan: slow, constant pressure (slightly faster steering, no special attack spam)
      if (bt === "titan") {
        // Nudge: titan is harder to kite by giving it a tiny extra steering speed.
        e.vx *= 1.06;
        e.vy *= 1.06;
      }

      // Rager: periodic charge dashes (boss version of charger).
      if (bt === "rager") {
        if ((e.chargeWindT || 0) > 0) {
          e.chargeWindT = Math.max(0, (e.chargeWindT || 0) - dt);
          e.vx = 0;
          e.vy = 0;
          if ((e.chargeWindT || 0) === 0) e.chargeT = 0.26;
        } else if ((e.chargeT || 0) > 0) {
          e.chargeT = Math.max(0, (e.chargeT || 0) - dt);
          const dashSp = (e.speed * spMul) * 4.6;
          e.vx = (e.chargeDx || toP.x) * dashSp;
          e.vy = (e.chargeDy || toP.y) * dashSp;
        } else {
          e.chargeCd = (e.chargeCd || 0) - dt;
          if ((e.chargeCd || 0) <= 0) {
            e.chargeCd = clamp(2.3 - state.wave * 0.035, 1.1, 2.3);
            e.chargeWindT = 0.32;
            e.chargeDx = toP.x;
            e.chargeDy = toP.y;
            game.audio?.warning?.("charge");
          }
        }
      }

      // Artillery: keeps distance, fires aimed spreads.
      if (bt === "artillery") {
        const dx = player.x - e.x;
        const dy = player.y - e.y;
        const d2 = dx * dx + dy * dy;
        const keepAway = 210;
        const dir = d2 < keepAway * keepAway ? norm(-dx, -dy) : toP;
        const spd = e.speed * spMul;
        e.vx = dir.x * spd;
        e.vy = dir.y * spd;

        e.bossCd = (e.bossCd || 0) - dt;
        if ((e.bossCd || 0) <= 0) {
          e.bossCd = clamp(2.1 - state.wave * 0.04, 0.95, 2.1);
          game.audio?.warning?.("bossShot");
          const aim = norm(player.x - e.x, player.y - e.y);
          const shots = 3 + (state.wave >= 18 ? 2 : state.wave >= 12 ? 1 : 0);
          const spread = 0.18 + Math.min(0.10, state.wave * 0.004);
          const baseAng = Math.atan2(aim.y, aim.x);
          for (let k = 0; k < shots; k++) {
            const t = shots === 1 ? 0 : (k / (shots - 1)) * 2 - 1; // -1..1
            const a = baseAng + t * spread;
            game.enemyBullets.push(
              createBullet({
                x: e.x,
                y: e.y,
                vx: Math.cos(a) * 260,
                vy: Math.sin(a) * 260,
                dmg: 10 + state.wave * 0.8,
                ttl: 2.4,
                r: 3,
                kind: "bossShot",
              }),
            );
          }
        }
      }

      // Summoner: periodic adds (original boss behavior).
      if (bt === "summoner") {
        e.summonCd -= dt;
        if (e.summonCd <= 0) {
          e.summonCd = clamp(2.6 - state.wave * 0.06, 1.1, 2.6);
          // spawn 2-5 minions near boss
          const n = 2 + ((Math.random() * 4) | 0);
          for (let k = 0; k < n; k++) {
            const r = Math.random();
            const kind = r < 0.55 ? "fast" : r < 0.85 ? "walker" : "charger";
            game.spawnEnemyNear(e.x, e.y, kind);
          }
        }
      }
    }

    // contact damage
    const rr = e.r + player.r;
    if (len2(e.x - player.x, e.y - player.y) <= rr * rr) {
      // exploder: kamikaze burst, then despawn
      if (e.kind === "exploder") {
        const boom = (e.boomDmg || 28) * dmgMul;
        player.hp -= boom;
        game.state.hitFlash = 0.22;
        game.state.damageAngle = Math.atan2(e.y - player.y, e.x - player.x);
        game.state.damageT = 0.95;
        game.state.damageKind = "explosive";
        game.audio?.hit?.(true);
        enemies.splice(i, 1);
        if (player.hp <= 0) {
          player.hp = 0;
          game.endGame();
          return;
        }
        continue;
      }
      const dps = CFG.contactDpsBase * e.dmgMul * (e.contactMul ?? 1) * contactMul * dmgMul;
      const dealt = dps * dt;
      player.hp -= dealt;
      if (e.affix === "vampire") {
        e.hp = Math.min(e.hpMax || e.hp, (e.hp || 0) + dealt * 0.65);
      }
      game.state.hitFlash = 0.12;
      game.state.damageAngle = Math.atan2(e.y - player.y, e.x - player.x);
      game.state.damageT = 0.8;
      game.state.damageKind = "contact";
      game.audio?.hit?.(true);
      if (player.hp <= 0) {
        player.hp = 0;
        game.endGame();
        return;
      }
    }
  }
}

export function updateBullets(dt, game) {
  const { bullets, enemies } = game;
  for (let i = bullets.length - 1; i >= 0; i--) {
    const b = bullets[i];
    b.ttl -= dt;
    const prevX = b.x;
    const prevY = b.y;
    if (b.ttl <= 0) {
      bullets.splice(i, 1);
      continue;
    }

    // Per-bullet cooldown (prevents multi-hit spam when overlapping).
    if (b.kind === "boomerang") {
      b.hitCd = Math.max(0, (b.hitCd || 0) - dt);
    }

    // Special bullets (not classic moving projectiles)
    if (b.kind === "laser") {
      if (!b.didHit) {
        b.didHit = true;
        const x0 = b.x;
        const y0 = b.y;
        const len = b.len || 520;
        const x1 = x0 + (b.vx || 0) * len;
        const y1 = y0 + (b.vy || 0) * len;
        const w = b.width || 10;

        for (let j = enemies.length - 1; j >= 0; j--) {
          const e = enemies[j];
          const rr = (e.r || 0) + w;
          if (distPointToSegmentSq(e.x, e.y, x0, y0, x1, y1) <= rr * rr) {
            e.lastHitKind = "laser";
            dealEnemyDamage(game, e, b.dmg || 0, b.vx || 0, b.vy || 0);
            // burn DOT (stored in bleed fields for now)
            if ((b.bleedT || 0) > 0 && (b.bleedDps || 0) > 0) {
              e.bleedT = Math.max(e.bleedT || 0, b.bleedT || 0);
              e.bleedDps = Math.max(e.bleedDps || 0, b.bleedDps || 0);
              e.dotKind = b.dotKind || e.dotKind;
              e.dotSourceKind = "laser";
            }
            if (e.hp <= 0) killEnemy(game, j, "laser");
          }
        }
      }
      continue;
    }

    if (b.kind === "tesla") {
      if (!b.didHit) {
        b.didHit = true;
        const points = [];
        const hit = new Set();
        let curX = b.x;
        let curY = b.y;
        // Avoid splicing `enemies` during chaining (keeps indices stable).
        const pendingKills = [];

        // first target: nearest to (curX,curY)
        for (let step = 0; step < (b.chains || 1); step++) {
          let best = null;
          let bestD2 = Infinity;
          for (let j = 0; j < enemies.length; j++) {
            const e = enemies[j];
            if (hit.has(e)) continue;
            const dx = e.x - curX;
            const dy = e.y - curY;
            const d2 = dx * dx + dy * dy;
            if (step > 0 && d2 > (b.chainR || 110) * (b.chainR || 110)) continue;
            if (d2 < bestD2) {
              bestD2 = d2;
              best = e;
            }
          }
          if (!best) break;
          const e = best;
          hit.add(e);
          points.push({ x: e.x, y: e.y });
          // diminishing damage along the chain
          const mul = 1 - step * 0.12;
          e.lastHitKind = "tesla";
          dealEnemyDamage(game, e, (b.dmg || 0) * clamp(mul, 0.35, 1));
          if (e.hp <= 0) pendingKills.push(e);
          curX = e.x;
          curY = e.y;
        }
        b.points = points;

        if (pendingKills.length) {
          for (let j = enemies.length - 1; j >= 0; j--) {
            if (pendingKills.includes(enemies[j])) killEnemy(game, j, "tesla");
          }
        }
      }
      continue;
    }

    if (b.kind === "mine") {
      b.armT = Math.max(0, (b.armT || 0) - dt);
      if ((b.armT || 0) > 0) continue;
      const tr = b.triggerR || 22;
      const tr2 = tr * tr;
      let triggered = false;
      for (let j = 0; j < enemies.length; j++) {
        const e = enemies[j];
        if (len2(e.x - b.x, e.y - b.y) <= (e.r + tr) * (e.r + tr)) {
          triggered = true;
          break;
        }
      }
      if (!triggered) continue;

      // explode
      const ex = b.x;
      const ey = b.y;
      const rr = b.explodeR || 60;
      for (let j = enemies.length - 1; j >= 0; j--) {
        const e = enemies[j];
        if (len2(e.x - ex, e.y - ey) <= (e.r + rr) * (e.r + rr)) {
          e.lastHitKind = "mine";
          dealEnemyDamage(game, e, b.dmg || 0);
          if (e.hp <= 0) killEnemy(game, j, "mine");
        }
      }
      // fx ring (rendered in renderer)
      if (bullets.length < CFG.maxBullets) {
        bullets.push({ x: ex, y: ey, vx: 0, vy: 0, dmg: 0, ttl: 0.22, r: 0, kind: "explosionFx", radius: rr });
      }
      // Mine blast cracks nearby walls too.
      const cc = worldToCell(ex, ey);
      const cr = Math.ceil(rr / CFG.cellPx) + 1;
      for (let oy = -cr; oy <= cr; oy++) {
        for (let ox = -cr; ox <= cr; ox++) {
          const cx = cc.cx + ox;
          const cy = cc.cy + oy;
          const wc = cellToWorldCenter(cx, cy);
          const d = Math.sqrt(len2(wc.x - ex, wc.y - ey));
          if (d > rr + CFG.cellPx * 0.8) continue;
          const mul = clamp(1 - d / Math.max(1, rr), 0.25, 1);
          damageWallCell({
            mapId: game.selectedMapId,
            cx,
            cy,
            damage: wallDamageFromBullet(b) * mul,
            brokenWalls: game.brokenWalls,
            wallDamage: game.wallDamage,
          });
        }
      }
      bullets.splice(i, 1);
      continue;
    }

    if (b.kind === "boomerang") {
      b.turnT = Math.max(0, (b.turnT || 0) - dt);
      if (!b.returning && (b.turnT || 0) <= 0) b.returning = true;
      if (b.returning) {
        const toP = norm(game.player.x - b.x, game.player.y - b.y);
        const sp = b.speed || 360;
        b.vx = toP.x * sp;
        b.vy = toP.y * sp;
        // catch: remove when back to player
        if (len2(b.x - game.player.x, b.y - game.player.y) <= 18 * 18) {
          bullets.splice(i, 1);
          continue;
        }
      }
    }

    // classic movement
    b.x += (b.vx || 0) * dt;
    b.y += (b.vy || 0) * dt;

    // Walls: mostly destroy, sometimes ricochet.
    const baseRicochetChance = clamp(0.32 + (game.player?.buffs?.ricochetChanceAdd || 0), 0, 0.82);
    const ricochetChance = b.kind === "flame" ? 0 : b.kind === "boomerang" ? 0.95 : baseRicochetChance;
    const wallRes = bounceOrDestroyBullet({
      bullet: b,
      prevX,
      prevY,
      mapId: game.selectedMapId,
      brokenWalls: game.brokenWalls,
      ricochetChance,
      dampMin: 0.70,
      dampMax: 0.88,
      onWallHit: () => {
        damageWallAtWorld({
          mapId: game.selectedMapId,
          x: b.x,
          y: b.y,
          damage: wallDamageFromBullet(b),
          brokenWalls: game.brokenWalls,
          wallDamage: game.wallDamage,
        });
      },
    });
    if (wallRes === "destroy") {
      bullets.splice(i, 1);
      continue;
    }

    // Boomerang: if we're in hit cooldown, skip collision this frame.
    if (b.kind === "boomerang" && (b.hitCd || 0) > 0) continue;

    for (let j = enemies.length - 1; j >= 0; j--) {
      const e = enemies[j];
      const rr = b.r + e.r;
      if (len2(b.x - e.x, b.y - e.y) <= rr * rr) {
        e.lastHitKind = b.kind || "bullet";
        dealEnemyDamage(game, e, b.dmg, b.vx || 0, b.vy || 0);

        // Knockback (shotgun)
        if ((b.knock || 0) > 0) {
          const dv = norm(b.vx || 0, b.vy || 0);
          e.x += dv.x * (b.knock || 0) * 0.08;
          e.y += dv.y * (b.knock || 0) * 0.08;
        }
        // Bleed (lance)
        if ((b.bleedT || 0) > 0 && (b.bleedDps || 0) > 0) {
          e.bleedT = Math.max(e.bleedT || 0, b.bleedT || 0);
          e.bleedDps = Math.max(e.bleedDps || 0, b.bleedDps || 0);
          e.dotKind = b.dotKind || e.dotKind;
          e.dotSourceKind = b.kind || e.dotSourceKind;
        }

        if (b.kind === "boomerang") {
          // Keep boomerang alive: it should return to player instead of being destroyed on first hits.
          b.hitCd = 0.075;
          if ((b.pierce || 0) > 0) b.pierce -= 1;
          // Once out of pierce, force return so it doesn't stay in the enemy pack.
          if ((b.pierce || 0) <= 0) {
            b.pierce = 0;
            b.returning = true;
            b.turnT = 0;
            const toP = norm(game.player.x - b.x, game.player.y - b.y);
            const sp = b.speed || 360;
            b.vx = toP.x * sp;
            b.vy = toP.y * sp;
          } else {
            // small deflect so successive hits feel like "bounces"
            b.vx *= 0.92;
            b.vy *= 0.92;
          }
        } else if (b.pierce > 0) {
          b.pierce -= 1;
          // tiny deflect
          b.vx *= 0.98;
          b.vy *= 0.98;
        } else {
          bullets.splice(i, 1);
        }

        if (e.hp <= 0) {
          killEnemy(game, j, b.kind);
        }
        break;
      }
    }
  }
}

export function updateEnemyBullets(dt, game) {
  const { enemyBullets, player } = game;
  const dmgMul = game.state.diff?.enemyDmgMul ?? 1;
  for (let i = enemyBullets.length - 1; i >= 0; i--) {
    const b = enemyBullets[i];
    b.ttl -= dt;
    const prevX = b.x;
    const prevY = b.y;
    if (b.ttl <= 0) {
      enemyBullets.splice(i, 1);
      continue;
    }

    // Special: telegraphed grenade marker -> explode
    if (b.kind === "grenade") {
      b.armT = Math.max(0, (b.armT || 0) - dt);
      if ((b.armT || 0) > 0) continue;
      const r = b.radius || 60;
      // FX ring (reuse bullets layer)
      if (game.bullets.length < CFG.maxBullets) {
        game.bullets.push({ x: b.x, y: b.y, vx: 0, vy: 0, dmg: 0, ttl: 0.26, r: 0, kind: "enemyExplosionFx", radius: r });
      }
      // Damage if player inside
      const rr = r + player.r;
      if (len2(b.x - player.x, b.y - player.y) <= rr * rr) {
        const boom = (b.dmg || 24) * dmgMul;
        player.hp -= boom;
        game.state.hitFlash = 0.22;
        game.state.damageAngle = Math.atan2(b.y - player.y, b.x - player.x);
        game.state.damageT = 0.95;
        game.state.damageKind = "aoe";
        game.audio?.hit?.(true);
        game.floats.push({ x: player.x, y: player.y - 18, ttl: 0.8, text: `-${Math.round(boom)}` });
        if (player.hp <= 0) {
          player.hp = 0;
          game.endGame();
          return;
        }
      }
      enemyBullets.splice(i, 1);
      continue;
    }

    // Special: stationary fire zone (DoT area)
    if (b.kind === "fireZone") {
      b.warmT = Math.max(0, (b.warmT || 0) - dt);
      const r = b.radius || 56;
      if ((b.warmT || 0) <= 0) {
        const rr = r + player.r;
        if (len2(b.x - player.x, b.y - player.y) <= rr * rr) {
          const dps = (b.dps || 10) * dmgMul;
          player.hp -= dps * dt;
          game.state.hitFlash = Math.max(game.state.hitFlash || 0, 0.06);
          game.state.damageAngle = Math.atan2(b.y - player.y, b.x - player.x);
          game.state.damageT = Math.max(game.state.damageT || 0, 0.25);
          game.state.damageKind = "fire";
          if (player.hp <= 0) {
            player.hp = 0;
            game.endGame();
            return;
          }
        }
      }
      continue;
    }

    // classic movement
    b.x += b.vx * dt;
    b.y += b.vy * dt;

    // Enemy bullets are destroyed on walls (fairness/readability).
    if (
      bulletHitWall(game.selectedMapId, b.x, b.y, game.brokenWalls) &&
      !bulletHitWall(game.selectedMapId, prevX, prevY, game.brokenWalls)
    ) {
      enemyBullets.splice(i, 1);
      continue;
    }

    const rr = b.r + player.r;
    if (len2(b.x - player.x, b.y - player.y) <= rr * rr) {
      player.hp -= b.dmg * dmgMul;
      game.state.hitFlash = 0.18;
      game.state.damageAngle = Math.atan2(b.y - player.y, b.x - player.x);
      game.state.damageT = 0.9;
      game.state.damageKind = b.kind === "bossShot" ? "bossShot" : b.kind === "spit" ? "spit" : "shot";
      game.audio?.hit?.(true);
      enemyBullets.splice(i, 1);
      game.floats.push({ x: player.x, y: player.y - 18, ttl: 0.8, text: `-${Math.round(b.dmg * dmgMul)}` });
      if (player.hp <= 0) {
        player.hp = 0;
        game.endGame();
        return;
      }
    }
  }
}
